import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  X,
  Eye,
  Download,
  FileText,
  Building2,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { documentService, Document, TipoDocumento } from "../../services/documentService";
import { formatDateFromDB } from "../../utils/dateUtils";

export function Documents() {
  const { tipoId } = useParams<{ tipoId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [tipo, setTipo] = useState<TipoDocumento | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/area-cliente");
    }
  }, [isAuthenticated, navigate]);

  // Busca o nome/descrição do tipo ao montar ou quando tipoId muda
  useEffect(() => {
    if (!tipoId) return;
    documentService.getStats().then((stats) => {
      const found = stats.find((t) => t.id === parseInt(tipoId));
      setTipo(found ?? null);
    }).catch(console.error);
  }, [tipoId]);

  useEffect(() => {
    if (tipoId) loadDocuments();
  }, [tipoId, searchTerm, dataInicio, dataFim, currentPage]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await documentService.list({
        tipoDocumentoId: tipoId ? parseInt(tipoId) : undefined,
        search: searchTerm || undefined,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
        page: currentPage,
        limit: 20,
      });
      setDocuments(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDataInicio("");
    setDataFim("");
    setCurrentPage(1);
  };

  const title = tipo?.nome ?? "Documentos";

  return (
    <>
      <Helmet>
        <title>{title} | Portal do Cliente</title>
      </Helmet>

      <div className="min-h-screen">
        <div className="py-8">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Portal
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              <span className="bg-gradient-to-r from-brand to-blue-500 bg-clip-text text-transparent">
                {title}
              </span>
            </h1>
            {tipo?.descricao && (
              <p className="text-muted-foreground mt-1">{tipo.descricao}</p>
            )}
          </motion.div>

          {/* Filtros */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col md:flex-row gap-4 mb-6 p-4 rounded-xl bg-card border border-border"
          >
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar documentos..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-background border border-input text-foreground placeholder-muted-foreground focus:border-brand focus:ring-1 focus:ring-brand transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">De:</span>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => { setDataInicio(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 rounded-lg bg-background border border-input text-foreground focus:border-brand focus:ring-1 focus:ring-brand transition-colors text-sm [color-scheme:dark]"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Até:</span>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => { setDataFim(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 rounded-lg bg-background border border-input text-foreground focus:border-brand focus:ring-1 focus:ring-brand transition-colors text-sm [color-scheme:dark]"
                />
              </div>
              {(searchTerm || dataInicio || dataFim) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Limpar
                </button>
              )}
            </div>
          </motion.div>

          {/* Tabela de documentos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl bg-card border border-border overflow-hidden"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-brand animate-spin" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhum documento encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Documento</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden lg:table-cell">Unidade</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden md:table-cell">Referência</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden lg:table-cell">Publicado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {documents.map((doc) => (
                      <tr key={doc.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-brand flex-shrink-0" />
                            <span className="text-foreground font-medium">{doc.nome}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-xs text-muted-foreground">
                            <Building2 className="w-3 h-3" />
                            {doc.unidade?.nome || "Matriz"}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-muted-foreground text-sm">
                            {formatDateFromDB(doc.dataReferencia)}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className="text-muted-foreground text-sm">
                            {formatDateFromDB(doc.createdAt)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => documentService.viewFile(doc.id)}
                              className="p-2 rounded-lg bg-brand/10 text-brand hover:bg-brand/20 transition-colors"
                              title="Visualizar"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => documentService.downloadFile(doc.id, doc.nome)}
                              className="p-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 border-t border-border">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <span className="text-muted-foreground text-sm">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Próxima
                </button>
              </div>
            )}
          </motion.div>

        </div>
      </div>
    </>
  );
}
